import React from 'react';
import styled from 'styled-components';
import { GridSection, Eyebrow, SectionTitle } from './GridSection';

const Role = styled.article`
  padding: 24px 0;
  border-top: 1px solid var(--rule);

  &:last-child { border-bottom: 1px solid var(--rule); }
`;

const Head = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px 24px;
  align-items: baseline;
  margin-bottom: 14px;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Title = styled.h3`
  font-family: 'EB Garamond', Georgia, serif;
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--ink);
  margin: 0 0 4px;
  letter-spacing: -0.005em;
`;

const Where = styled.div`
  font-style: italic;
  font-size: 1rem;
  color: var(--ink-soft);
  overflow-wrap: anywhere;

  .sep { color: var(--ink-muted); margin: 0 6px; font-style: normal; }
`;

const Date = styled.div`
  font-style: italic;
  font-size: 0.95rem;
  color: var(--ink-muted);
  text-align: right;

  @media (max-width: 600px) { text-align: left; }
`;

const Bullets = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 12px;

  li {
    position: relative;
    padding: 4px 0 4px 20px;
    font-size: 1.04rem;
    line-height: 1.65;
    color: var(--ink-soft);
    overflow-wrap: anywhere;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 14px;
      width: 10px;
      height: 1px;
      background: var(--ink-muted);
    }

    strong { color: var(--ink); font-weight: 500; }
  }
`;

const Tech = styled.div`
  font-style: italic;
  font-size: 0.92rem;
  color: var(--ink-muted);
  overflow-wrap: anywhere;
`;

const experiences = [
  {
    company: 'Trinity Life Sciences',
    role: 'Software Engineer',
    location: 'New York, NY',
    date: 'Jun – Dec 2025',
    bullets: [
      'Built the Node.js <strong>REST API layer</strong>: routing, validation, auth, and error responses that don\'t lie about what went wrong',
      'Designed the <strong>data layer on PostgreSQL and Redis</strong>; spent real time on connection pooling, query plans, and schema versioning',
      'Wired up distributed tracing, custom metrics, and structured logging. Triage time dropped a lot once the on-call could actually see the system'
    ],
    tech: 'Node.js, PostgreSQL, Redis, REST, microservices, CI/CD'
  },
  {
    company: 'BlueLake',
    role: 'Software Engineer',
    location: 'Chicago, IL (remote)',
    date: 'Jan 2022 – Sep 2024',
    bullets: [
      'Designed the <strong>GCP/Kubernetes platform</strong> behind 10M+ daily requests for 500K+ users. 99.99% uptime, 40% lower spend after the rework',
      'Built the <strong>Django and Java REST APIs</strong> on PostgreSQL handling 50K req/min; multi-layer Redis caching pulled p95 latency under 100ms',
      'Built the <strong>Kafka event pipeline</strong> with exactly-once semantics, processing 5M+ events/day at 99.9% reliability',
      'Shipped the <strong>Stripe integration</strong>: idempotent endpoints, webhook signature validation, retries that don\'t double-charge',
      'Wired up Prometheus, Grafana, and Sentry. MTTR roughly halved once we stopped grepping logs by hand'
    ],
    tech: 'Python, Django, Java, PostgreSQL, Kafka, Kubernetes, GCP'
  },
  {
    company: 'World Wildlife Fund',
    role: 'Software Engineering Intern',
    location: 'Remote',
    date: 'Jun – Sep 2021',
    bullets: [
      'Trained a ResNet in PyTorch on a curated 10K+ image dataset for <strong>bird species recognition</strong>; landed around 90% accuracy',
      'Served the model as a <strong>real-time classification API</strong> on AWS Lambda with S3-hosted artifacts'
    ],
    tech: 'PyTorch, FastAPI, AWS Lambda, S3, computer vision'
  },
  {
    company: 'Virufy',
    role: 'Software Engineering Intern',
    location: 'San Francisco, CA (remote)',
    date: 'Apr – Jun 2020',
    bullets: [
      'Worked on a <strong>COVID-19 cough audio classifier</strong>; helped crowdsource 5,000+ clinical samples, growing the dataset by ~60%',
      'Built MFCC and spectrogram feature extraction and trained the CNNs in PyTorch'
    ],
    tech: 'PyTorch, audio DSP, CNNs'
  }
];

function WorkExperience() {
  return (
    <GridSection id="work">
      <Eyebrow>iv — work</Eyebrow>
      <SectionTitle>Experience.</SectionTitle>
      {experiences.map((e, i) => (
        <Role key={i}>
          <Head>
            <div>
              <Title>{e.role}</Title>
              <Where>{e.company}<span className="sep">·</span>{e.location}</Where>
            </div>
            <Date>{e.date}</Date>
          </Head>
          <Bullets>
            {e.bullets.map((b, j) => (
              <li key={j} dangerouslySetInnerHTML={{ __html: b }} />
            ))}
          </Bullets>
          <Tech>{e.tech}</Tech>
        </Role>
      ))}
    </GridSection>
  );
}

export default WorkExperience;
